import { z } from "zod";

// Blog post validation schema with comprehensive security constraints
export const blogPostSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: "Title is required" })
    .max(200, { message: "Title must be less than 200 characters" }),
  
  slug: z
    .string()
    .trim()
    .min(1, { message: "Slug is required" })
    .max(200, { message: "Slug must be less than 200 characters" })
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, { 
      message: "Slug must be lowercase letters, numbers, and hyphens only" 
    }),
  
  excerpt: z
    .string()
    .trim()
    .max(500, { message: "Excerpt must be less than 500 characters" })
    .optional()
    .or(z.literal("")),
  
  content: z
    .string()
    .trim()
    .min(1, { message: "Content is required" })
    .max(50000, { message: "Content must be less than 50,000 characters" }),
  
  featured_image: z
    .string()
    .url({ message: "Featured image must be a valid URL" })
    .optional()
    .or(z.literal("")),
  
  author: z
    .string()
    .trim()
    .min(1, { message: "Author name is required" })
    .max(100, { message: "Author name must be less than 100 characters" }),
  
  author_bio: z
    .string()
    .trim()
    .max(500, { message: "Author bio must be less than 500 characters" })
    .optional()
    .or(z.literal("")),
  
  author_image: z
    .string()
    .url({ message: "Author image must be a valid URL" })
    .optional()
    .or(z.literal("")),
  
  category_id: z
    .string()
    .uuid({ message: "Invalid category ID" })
    .optional()
    .or(z.literal(""))
    .nullable(),
  
  status: z.enum(["draft", "published"], {
    message: "Status must be either 'draft' or 'published'",
  }),
  
  meta_title: z
    .string()
    .trim()
    .max(60, { message: "Meta title must be less than 60 characters for SEO" })
    .optional()
    .or(z.literal("")),
  
  meta_description: z
    .string()
    .trim()
    .max(160, { message: "Meta description must be less than 160 characters for SEO" })
    .optional()
    .or(z.literal("")),
  
  canonical_url: z
    .string()
    .url({ message: "Canonical URL must be a valid URL" })
    .optional()
    .or(z.literal("")),
});

export type BlogPostFormData = z.infer<typeof blogPostSchema>;
